import React, {useEffect, useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {AgGridReact} from "ag-grid-react";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import GarrisonService from "../../../../services/dispatch/garrison-service.js";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import Loader from "../../../../components/template/loader.jsx";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const hasValue = value => value !== null && value !== undefined && value !== "";

const getAssignmentType = item => {
    if (item.assignmentSource === "ATTENDANCE") return "Atendimento";
    if (item.assignmentSource === "TASK") return "Tarefa";
    return "";
};

const GarrisonAssignmentsModal = ({payload, onClose, onDispatchAssignment}) => {
    const navigate = navigateHook();
    const queryClient = useQueryClient();
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
    const [selectionError, setSelectionError] = useState("");
    const requested = Boolean(payload);
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId;
    const assignmentData = payload?.assignmentData;
    const hasAssignmentData = assignmentData !== undefined;
    const selectForDispatch = payload?.selectForDispatch === true;
    const dispatchStatus = garrison?.dispatchStatus === true || hasValue(garrison?.dispatchId);

    const assignmentQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonAssignmentDetails(garrisonId),
        queryFn: () => GarrisonService.getGarrisonByIdToView(garrisonId),
        enabled: requested && hasValue(garrisonId) && !hasAssignmentData,
    });
    const isReady = hasAssignmentData || (!assignmentQuery.isFetching && (assignmentQuery.isSuccess || assignmentQuery.isError));
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    const removeAssignmentMutation = useMutation({
        mutationFn: DispatchService.removeAssignmentItem,
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: DASHBOARD_QUERY_KEYS.garrisonAssignmentDetails(garrisonId)});
            closeModal();
            projectUtils.swalSuccessMessage("success", "Sucesso", "Item removido da atribuição com sucesso.", "Ok");
        },
        onError: error => {
            console.error("Erro ao remover item da atribuição pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível remover o item da atribuição.";
            projectUtils.swalErrorMessage("Erro", message, "Ok");
        },
    });

    const result = assignmentData ?? assignmentQuery.data;
    const assignmentRows = Array.isArray(result?.assignment) ? result.assignment : [];
    const garrisonName = result?.garrison?.name ?? garrison?.garrisonName ?? "";

    useEffect(() => {
        setSelectedAssignmentId(null);
        setSelectionError("");
    }, [payload]);

    const removeAssignmentItem = item => {
        projectUtils.swalMessage(
            "warning",
            "Remover",
            "Deseja remover este item da atribuição?",
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                removeAssignmentMutation.mutate(item.id);
            },
            true,
            "warning",
            true
        );
    };

    const selectAssignment = item => {
        setSelectedAssignmentId(currentId => currentId === item.id ? null : item.id);
        setSelectionError("");
    };

    const dispatchSelectedAssignment = () => {
        const selectedAssignment = assignmentRows.find(item => item.id === selectedAssignmentId);

        if (!selectedAssignment) {
            setSelectionError("Selecione um item para despachar.");
            return;
        }

        onDispatchAssignment?.(selectedAssignment);
    };

    const openGarrison = () => {
        const url = `${DESPACHO_WEB_URL}/garrison/form/${garrisonId}?redir=despacho/dashboard`
        navigate(url, true);
    }

    const openAssignment = item => {
        const routeBuilders = {
            ATTENDANCE: id => ({
                url: `${DESPACHO_WEB_URL}/attendance/form/${id}?redir=despacho/dashboard`,
            }),
            "TASK:ATTENDANCE": id => ({
                url: `${DESPACHO_WEB_URL}/attendance/form/${id}`,
            }),
            "TASK:SOS_SCHEDULED_VISIT": id => ({
                url: `/sos/citizen-scheduled-visit?schedule_id=${id}`,
            }),
            "TASK:JANITORIAL": id => ({
                url: `/janitorial/form/${id}`,
            }),
        }

        const routeKey = item.assignmentSource === "TASK" ? `TASK:${item.taskSource}` : item.assignmentSource;
        const routeBuilder = routeBuilders[routeKey];

        if (!routeBuilder) return;

        const {url} = routeBuilder(item.taskSourceId);
        navigate(url, true);
    };

    const hasAssignment = item => item.assignmentSource === "ATTENDANCE" || (item.assignmentSource === "TASK");

    const columnDefs = useMemo(() => [
        {
            headerName: "Tipo",
            width: 150,
            valueGetter: ({data}) => getAssignmentType(data),
        },
        {
            field: "assignmentDescription",
            headerName: "Conteúdo",
            minWidth: 180,
            flex: 1,
            wrapText: true,
            autoHeight: true,
            cellRenderer: ({data, value}) => hasAssignment(data) ? (
                <button
                    type="button"
                    className="cursor-pointer text-left text-[#428bca] hover:underline"
                    onClick={() => openAssignment(data)}
                >
                    {value ?? ""}
                </button>
            ) : value ?? "",
        },
        {
            field: "reasonText",
            headerName: "Natureza",
            minWidth: 220,
            flex: 2,
            wrapText: true,
            autoHeight: true,
        },
        {
            headerName: "Ações",
            width: dispatchStatus ? 110 : 210,
            sortable: false,
            cellRenderer: ({data}) => (
                <div className="flex h-full items-center justify-center gap-2">
                    <Button
                        target="danger"
                        disabled={removeAssignmentMutation.isPending}
                        className="h-6! px-2! text-[11px]"
                        onClick={() => removeAssignmentItem(data)}
                        actionFeedbackType="delete"
                    >
                        Remover
                    </Button>
                    {!dispatchStatus && (
                        <Button className="h-6! px-2! text-[11px]" onClick={() => onDispatchAssignment?.(data)}>
                            Despachar
                        </Button>
                    )}
                </div>
            ),
        },
    ], [dispatchStatus, onDispatchAssignment, removeAssignmentMutation.isPending]);

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title={selectForDispatch ? "Selecione um item atribuído para despachar" : "Atribuições da guarnição"} className="w-full max-w-6xl" isCloseClick={false} animated={false}>
                <Modal.Body className="flex max-h-[calc(100vh-180px)] flex-col gap-4 overflow-auto">
                    <div>
                        <h3 className="mb-1 text-[16px] font-semibold text-[#676a6c]">Guarnição</h3>
                        <p className="cursor-pointer text-[#428bca] hover:underline" onClick={openGarrison}>
                            {garrisonName}
                        </p>
                    </div>

                    {assignmentQuery.isError && (
                        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            Não foi possível carregar as atribuições da guarnição.
                        </div>
                    )}

                    {!assignmentQuery.isFetching && !assignmentQuery.isError && assignmentRows.length === 0 && (
                        <div className="text-center text-sm text-[#676a6c]">A guarnição não possui atribuições.</div>
                    )}

                    {!assignmentQuery.isFetching && !assignmentQuery.isError && assignmentRows.length > 0 && selectForDispatch && (
                        <div className="flex flex-col gap-3">
                            {assignmentRows.map(item => (
                                <InputCheckbox
                                    key={item.id}
                                    id={`assignment-item-${item.id}`}
                                    name="assignment-item"
                                    checked={selectedAssignmentId === item.id}
                                    onChange={() => selectAssignment(item)}
                                    label={(
                                        <span>
                                            <strong>{item.assignmentSourceText ?? getAssignmentType(item)}</strong>
                                            {` • ${item.assignmentDescription ?? ""} • ${item.reasonText ?? ""}`}
                                        </span>
                                    )}
                                />
                            ))}

                            {selectionError && (
                                <div className="rounded border border-red-200 bg-red-50 p-3 text-center text-sm text-red-700">
                                    {selectionError}
                                </div>
                            )}
                        </div>
                    )}

                    {!assignmentQuery.isFetching && !assignmentQuery.isError && assignmentRows.length > 0 && !selectForDispatch && (
                        <AgGridReact
                            domLayout="autoHeight"
                            rowData={assignmentRows}
                            columnDefs={columnDefs}
                            defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                            overlayNoRowsTemplate="Nenhuma atribuição."
                        />
                    )}
                </Modal.Body>
                {selectForDispatch && (
                    <Modal.Footer>
                        <Button type="button" onClick={dispatchSelectedAssignment} actionFeedbackType='save'>Salvar</Button>
                    </Modal.Footer>
                )}
            </Modal>
        </>
    );
};

export default GarrisonAssignmentsModal;